import type { UIItemContainerKind } from '@sorrem-client/types';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ContextMenu, type ContextMenuItem } from '../ContextMenu';

export interface ItemTransferSelection {
    itemId: string;
    container: UIItemContainerKind;
}

/**
 * An item's right-click menu (#267), built by the page for the item it opens
 * on; `selectItem` selects it, for Details.
 */
export type ItemMenuBuilder = (
    selection: ItemTransferSelection,
    transfer: { readonly selectItem: (itemId: string, container: UIItemContainerKind) => void },
) => readonly ContextMenuItem[];

interface ItemTransferTarget {
    container: UIItemContainerKind;
    index: number;
}

interface ItemTransferContextValue {
    selectedItemId: string | null;
    toggleSelectedItem: (itemId: string, container: UIItemContainerKind) => void;
    /** Selects an item, or keeps it selected if it already is. */
    selectItem: (itemId: string, container: UIItemContainerKind) => void;
    clearSelectedItem: () => void;
    moveTo: (container: UIItemContainerKind, index: number) => void;
    /** Opens the item's right-click menu beside `anchor`; absent where the page has none. */
    openItemMenu?: (itemId: string, container: UIItemContainerKind, anchor: HTMLElement) => void;
}

interface SelectedItemState {
    id: string | null;
    container: UIItemContainerKind | null;
}

interface ItemTransferProviderProps {
    children: ReactNode;
    onMoveTo?: (selection: ItemTransferSelection, target: ItemTransferTarget) => void;
    canMoveTo?: (selection: ItemTransferSelection, target: ItemTransferTarget) => boolean;
    /** The page's item menu; without it, right-clicking an item does nothing. */
    itemMenu?: ItemMenuBuilder;
}

const ItemTransferContext = createContext<ItemTransferContextValue | undefined>(undefined);

/**
 * Tracks selected item state and exposes container-to-container move logic,
 * and owns the page's item right-click menu: every slot opens it the same
 * way, and the page says what it holds (`itemMenu`).
 */
export function ItemTransferProvider({ children, onMoveTo, canMoveTo, itemMenu }: ItemTransferProviderProps): JSX.Element {
    const [menuSelection, setMenuSelection] = useState<ItemTransferSelection | null>(null);
    const menuAnchorRef = useRef<HTMLElement | null>(null);
    const closeItemMenu = useCallback((): void => setMenuSelection(null), []);
    const openItemMenu = useCallback((itemId: string, container: UIItemContainerKind, anchor: HTMLElement): void => {
        menuAnchorRef.current = anchor;
        setMenuSelection({ itemId, container });
    }, []);

    const [selectedItem, setSelectedItem] = useState<SelectedItemState>({
        id: null,
        container: null,
    });

    const toggleSelectedItem = useCallback((itemId: string, container: UIItemContainerKind): void => {
        setSelectedItem((previousValue) => {
            if (previousValue.id === itemId && previousValue.container === container) {
                return {
                    id: null,
                    container: null,
                };
            }

            return {
                id: itemId,
                container,
            };
        });
    }, []);

    const selectItem = useCallback((itemId: string, container: UIItemContainerKind): void => {
        setSelectedItem((previousValue) => (
            previousValue.id === itemId && previousValue.container === container ? previousValue : { id: itemId, container }
        ));
    }, []);

    const clearSelectedItem = useCallback((): void => {
        setSelectedItem({
            id: null,
            container: null,
        });
    }, []);

    const moveTo = useCallback((container: UIItemContainerKind, index: number): void => {
        if (selectedItem.id === null || selectedItem.container === null) {
            return;
        }

        const selection: ItemTransferSelection = {
            itemId: selectedItem.id,
            container: selectedItem.container,
        };
        const target: ItemTransferTarget = {
            container,
            index,
        };

        // Every target: a move into the carried slots can still pull an item into the loadout (a swap).
        if (canMoveTo !== undefined && !canMoveTo(selection, target)) {
            return;
        }

        if (onMoveTo !== undefined) {
            onMoveTo(selection, target);
        }

        clearSelectedItem();
    }, [canMoveTo, clearSelectedItem, onMoveTo, selectedItem.container, selectedItem.id]);

    const contextValue = useMemo<ItemTransferContextValue>(() => ({
        selectedItemId: selectedItem.id,
        toggleSelectedItem,
        selectItem,
        clearSelectedItem,
        moveTo,
        ...(itemMenu !== undefined ? { openItemMenu } : {}),
    }), [selectedItem.id, toggleSelectedItem, selectItem, clearSelectedItem, moveTo, itemMenu, openItemMenu]);

    return (
        <ItemTransferContext.Provider value={contextValue}>
            {children}
            {itemMenu !== undefined && menuSelection !== null && (
                <ContextMenu
                    open
                    anchorRef={menuAnchorRef as RefObject<HTMLElement>}
                    items={itemMenu(menuSelection, { selectItem })}
                    onClose={closeItemMenu}
                    ariaLabel='Item options'
                />
            )}
        </ItemTransferContext.Provider>
    );
}

export function useItemTransferContext(): ItemTransferContextValue {
    const context = useContext(ItemTransferContext);
    if (context === undefined) {
        throw new Error('useItemTransferContext must be used within an ItemTransferProvider');
    }

    return context;
}
